import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, Maximize2, Play } from "lucide-react";
import { fileUrl } from "@/lib/format";

export type GalleryItem = {
  key: string;
  alt?: string;
  mimeType?: string;
};

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv)$/i;

export function isVideoItem(item: GalleryItem): boolean {
  if (item.mimeType?.startsWith("video/")) return true;
  return VIDEO_EXT.test(item.key);
}

type ImageGalleryProps = {
  items: GalleryItem[];
  /** "carousel" = main image + thumbnail strip below; "grid" = static grid */
  variant?: "carousel" | "grid";
};

/**
 * Multi-image gallery with thumbnail strip + click-to-fullscreen lightbox.
 * Keyboard nav (arrows + escape) inside the lightbox.
 */
export function ImageGallery({ items, variant = "carousel" }: ImageGalleryProps) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (items.length === 0) {
    return (
      <div className="aspect-[4/3] bg-brand-100 grid place-items-center text-brand-300 text-sm">
        Nincs feltöltött kép
      </div>
    );
  }

  const next = useCallback(
    () => setActive((i) => (i + 1) % items.length),
    [items.length],
  );
  const prev = useCallback(
    () => setActive((i) => (i - 1 + items.length) % items.length),
    [items.length],
  );

  if (variant === "grid") {
    return (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map((img, i) => (
            <button
              key={img.key + i}
              type="button"
              onClick={() => {
                setActive(i);
                setLightboxOpen(true);
              }}
              className="group relative aspect-[4/3] overflow-hidden bg-brand-200"
            >
              {isVideoItem(img) ? (
                <>
                  <video
                    src={fileUrl(img.key)}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute inset-0 grid place-items-center bg-black/25">
                    <span className="w-10 h-10 grid place-items-center rounded-full bg-white/90 text-brand-900">
                      <Play size={16} className="ml-0.5" />
                    </span>
                  </span>
                </>
              ) : (
                <img
                  src={fileUrl(img.key)}
                  alt={img.alt ?? ""}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
            </button>
          ))}
        </div>
        {lightboxOpen && (
          <Lightbox
            items={items}
            index={active}
            onClose={() => setLightboxOpen(false)}
            onNext={next}
            onPrev={prev}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Main image / video */}
        <div className="relative group aspect-[16/10] overflow-hidden bg-brand-200">
          {isVideoItem(items[active]) ? (
            <video
              key={items[active].key}
              src={fileUrl(items[active].key)}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-black"
            />
          ) : (
            <img
              key={items[active].key}
              src={fileUrl(items[active].key)}
              alt={items[active].alt ?? ""}
              className="w-full h-full object-cover"
            />
          )}

          {/* Open lightbox button */}
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="absolute top-4 right-4 w-10 h-10 grid place-items-center bg-white/90 hover:bg-white text-brand-700 transition-colors"
            aria-label="Teljes képernyő"
          >
            <Maximize2 size={16} />
          </button>

          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-white/90 hover:bg-white text-brand-700 transition-colors opacity-0 group-hover:opacity-100"
                aria-label="Előző"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-white/90 hover:bg-white text-brand-700 transition-colors opacity-0 group-hover:opacity-100"
                aria-label="Következő"
              >
                <ChevronRight size={20} />
              </button>

              {/* Counter */}
              <div className="absolute bottom-4 right-4 bg-black/65 text-white text-xs uppercase tracking-widish px-3 py-1.5 font-semibold">
                {String(active + 1).padStart(2, "0")} /{" "}
                {String(items.length).padStart(2, "0")}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {items.length > 1 && (
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {items.map((img, i) => (
              <button
                key={img.key + i}
                type="button"
                onClick={() => setActive(i)}
                className={`aspect-[4/3] overflow-hidden bg-brand-200 border-2 transition-colors ${
                  active === i
                    ? "border-accent"
                    : "border-transparent hover:border-brand-300"
                }`}
              >
                {isVideoItem(img) ? (
                  <span className="relative block w-full h-full">
                    <video
                      src={fileUrl(img.key)}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute inset-0 grid place-items-center bg-black/25 text-white">
                      <Play size={14} />
                    </span>
                  </span>
                ) : (
                  <img
                    src={fileUrl(img.key)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          items={items}
          index={active}
          onClose={() => setLightboxOpen(false)}
          onNext={next}
          onPrev={prev}
        />
      )}
    </>
  );
}

export function Lightbox({
  items,
  index,
  onClose,
  onNext,
  onPrev,
}: {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}) {
  // Keyboard navigation
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onPrev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNext, onPrev]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Counter top-center */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 text-white/80 text-xs uppercase tracking-widish font-semibold">
        {String(index + 1).padStart(2, "0")} /{" "}
        {String(items.length).padStart(2, "0")}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 w-12 h-12 grid place-items-center text-white/80 hover:text-accent transition-colors"
        aria-label="Bezár"
      >
        <X size={24} />
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center text-white/80 hover:text-accent transition-colors"
            aria-label="Előző"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center text-white/80 hover:text-accent transition-colors"
            aria-label="Következő"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}

      {isVideoItem(item) ? (
        <video
          key={item.key}
          src={fileUrl(item.key)}
          controls
          autoPlay
          playsInline
          className="max-w-[92vw] max-h-[88vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          key={item.key}
          src={fileUrl(item.key)}
          alt={item.alt ?? ""}
          className="max-w-[92vw] max-h-[88vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}
